import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  Modal,
  CircularProgress,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import { toast } from 'react-toastify';

import LandingPageNav from '../../components/landingpage/LandingPageNav';
import Footer from '../../components/Footer/footer';

import { apigetPublic, apidelete } from '../../service/api';

// 🔹 EDIT MODAL
import BuilderIntakeEditModal from './Edit';

// -----------------------------
export default function BuilderIntakeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const [intake, setIntake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openEdit, setOpenEdit] = useState(false);

  const canEdit = user?.role === 'admin' || user?.role === 'marketing';

  // -----------------------------------
  // Fetch Intake
  // -----------------------------------
  const fetchIntake = async () => {
    setLoading(true);
    try {
      const res = await apigetPublic(`intake/${id}`);
      if (res?.status === 200) {
        setIntake(res.data);
      } else {
        setIntake(null);
      }
    } catch (err) {
      toast.error('Failed to load intake');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntake();
  }, [id]);

  // -----------------------------------
  // Delete Intake
  // -----------------------------------
  const handleDelete = async () => {
    if (!window.confirm('Delete this builder intake form?')) return;

    try {
      const res = await apidelete(`intake/${id}`);
      if (res?.status === 200) {
        toast.success('Builder intake deleted');
        navigate('/dashboard/intake');
      }
    } catch (err) {
      toast.error('Failed to delete intake');
    }
  };

  if (loading) {
    return (
      <Modal open>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          p: 4,
          textAlign: 'center'
        }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading Intake...</Typography>
        </Box>
      </Modal>
    );
  }

  if (!intake) {
    return (
      <Typography sx={{ mt: 10, textAlign: 'center' }}>
        Builder intake not found.
      </Typography>
    );
  }

  return (
    <>
      <LandingPageNav />

      <Box
        sx={{
          backgroundImage:
            'url(https://res.cloudinary.com/ddhrdxc7d/image/upload/v1763995325/Background_Image_-_Blog_Pages_and_individual_LO_pages_1_w6e9ef.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'rgba(245,245,245,.85)',
          },
        }}
      >
        {/* Admin Controls */}
        {canEdit && (
          <Box sx={{ p: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setOpenEdit(true)}
            >
              Edit Intake
            </Button>

            <Button
              variant="outlined"
              color="error"
              sx={{ ml: 2 }}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Box>
        )}

        <Container sx={{ pt: '100px', pb: 6, position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
            <Box>
              <img
                src={intake.logoSrc}
                alt={intake.title}
                style={{
                  width: 260,
                  height: 160,
                  objectFit: 'contain',
                  background: '#fff',
                  padding: 16,
                  borderRadius: 12,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }}
              />
            </Box>

            <Box flex={1}>
              <Typography variant="h3" fontWeight={700}>
                {intake.title}
              </Typography>

              <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
                Slug: {intake.slug}
              </Typography>

              <Typography sx={{ mt: 1 }}>
                Endpoint: <strong>{intake.endpoint}</strong>
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Chip
                  label={intake.isActive ? 'Active' : 'Inactive'}
                  color={intake.isActive ? 'success' : 'default'}
                />
              </Box>

              {intake.subtitle && (
                <Typography sx={{ mt: 2 }}>{intake.subtitle}</Typography>
              )}
            </Box>
          </Stack>

          {/* Sections */}
          <Box sx={{ mt: 6 }}>
            <Section title="Email Recipients" items={intake.emailTo} />
            <Section title="Loan Officers" items={intake.loanOfficers} />
            <Section title="Agent Names" items={intake.agentNames} />
            <Section title="Communities" items={intake.communities} />
            <Section title="Forward Commitments" items={intake.forwardCommitments} />
            <Section title="Urgency Options" items={intake.urgency} />
            <Section title="Date Fields" items={intake.dateFields} />
            <Section
              title="Custom Fields"
              items={(intake.customFields || []).map((f) => {
                const bits = [f.label, `type: ${f.type || 'text'}`];
                if (f.required) bits.push('required');
                if (f.type === 'select' && f.options?.length) bits.push(`options: ${f.options.join(', ')}`);
                return bits.join(' — ');
              })}
            />
            <Section title="Hidden Built-in Fields" items={intake.hiddenFields} />
          </Box>
        </Container>
      </Box>

      {/* 🔹 EDIT MODAL */}
      <BuilderIntakeEditModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        intakeData={intake}
        onUpdate={fetchIntake}
      />

      <Footer />
    </>
  );
}

/* -----------------------------
   Reusable List Section
----------------------------- */
function Section({ title, items }) {
  if (!items || items.length === 0) return null;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight={700} mb={1}>
        {title}
      </Typography>

      {items.map((item, idx) => (
        <Typography key={idx} sx={{ ml: 1 }}>
          • {item}
        </Typography>
      ))}
    </Paper>
  );
}
